import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import TopNav from '../components/TopNav';
import SideIcons from '../components/SideIcons';
import ComposeModal from '../components/ComposeModal';
import CommentThreadList from '../components/CommentsPanel/CommentThreadList';
import Footer from '../components/Footer';
import RawResponseModal from '../components/RawResponseModal/RawResponseModal';
import PostTypeIndicator from '../components/PostTypeIndicator/PostTypeIndicator';
import { postsAPI, inboxAPI, platformsAPI } from '../api';
import { showToast, showErrorToast } from '../components/Toast/toastUtils';
import './Inbox.css';

const PAGE_SIZE = 20;
const AUTO_REFRESH_MS = 5000; // Poll inbox every 5 seconds (webhook platforms: FB, IG, Threads)
const MAX_ITEMS_TO_TRACK = 500; // Limit tracked IDs to prevent memory bloat

// Auto-sync polls EVERY connected platform on an interval - webhook and
// non-webhook alike. Webhooks only deliver a subset of events and only once a
// subscription is installed, so a webhook-kind platform with no subscription
// (e.g. Threads) would otherwise never refresh. Webhooks are left untouched;
// this is purely additive client-driven polling.
// Backend rate limit is 60s default per connection (via SyncPolicy.min_sync_interval_seconds),
// so a shorter interval is effectively a no-op (connections return 'skipped_rate_limit').
const NON_WEBHOOK_SYNC_MS = 60000; // Match backend rate limit (60s)

// Platform colors for badges
const PLATFORM_COLORS = {
  reddit: '#FF4500',
  tiktok: '#000000',
  instagram: '#E4405F',
  facebook: '#1877F2',
  twitter: '#1DA1F2',
  youtube: '#FF0000',
  linkedin: '#0A66C2'
};

/**
 * Name/avatar of the party a conversation is with.
 *
 * For message threads the row's own sender is the thread root, which may be an
 * outbound message; prefer the counterpart recorded by the backend.
 */
const conversationTitle = (item) => {
  if (!item) return '';
  if (item.item_type === 'message') {
    return item.counterpart_display_name || item.counterpart_username
      || item.sender_display_name || item.sender_username || 'Conversation';
  }
  return item.sender_display_name || item.sender_username || 'Unknown';
};

const conversationAvatar = (item) => (
  item?.item_type === 'message'
    ? (item.counterpart_avatar_url || item.sender_avatar_url)
    : item?.sender_avatar_url
);

/**
 * True when a message was authored by the connected account.
 *
 * The backend marks outbound items with sender_platform_id 'self' (and
 * sender_username 'you'); older rows may only carry the username marker.
 */
const isOutboundMessage = (msg) => (
  msg?.sender_platform_id === 'self' || msg?.sender_username === 'you'
);

/**
 * Stable identity for an inbox row.
 *
 * The DB id is authoritative and unique. `platform_item_id` alone is not
 * enough: it is unique only per connection, so a derived connection (facebook
 * created from instagram_via_facebook) could collide with its source.
 */
const itemKey = (item) => (
  item.id != null
    ? `db:${item.id}`
    : `${item.platform_connection_id}:${item.platform_item_id}`
);

/**
 * Activity timestamp for an inbox head item.
 *
 * Mirrors the backend's ordering key — `COALESCE(conversation.last_activity_at,
 * platform_created_at)` — falling back to `updated_at` when neither is present.
 */
const itemActivityTs = (item) => {
  const raw = item.last_activity_at || item.platform_created_at || item.updated_at;
  const ts = raw ? Date.parse(raw) : NaN;
  return Number.isNaN(ts) ? 0 : ts;
};

/**
 * Return a copy of the inbox list sorted newest-activity-first (id desc as a
 * stable tie-break, matching the backend's `activity DESC, id DESC`).
 *
 * The list is rebuilt on every poll/merge, and a merge that only concatenates
 * (delta items first, then the rest) reorders threads arbitrarily — e.g. an old
 * thread whose `Conversation.updated_at` was bumped by a routine re-upsert
 * would jump above a genuinely newer thread. Sorting here keeps the visible
 * order authoritative and aligned with the server's initial-load order.
 */
const sortItemsByActivity = (items) =>
  [...items].sort((a, b) => {
    const diff = itemActivityTs(b) - itemActivityTs(a);
    if (diff !== 0) return diff;
    return (b.id || 0) - (a.id || 0);
  });

/**
 * Merge a freshly-fetched first page of children into the currently-loaded
 * children tree WITHOUT discarding already-expanded subtrees.
 *
 * A selected comment's `_children` accumulates frontend-only state that a
 * fresh page does not carry: nested `.replies` injected by expanding deeper
 * levels (`handleLoadMoreReplies`/`updateCommentReplies`) and the appended
 * pages from the top-level "Load more replies". Replacing `_children` with a
 * bare first page would collapse an expanded 3-level thread back to its root.
 *
 * Strategy: keep existing nodes in order (refreshing their server metadata but
 * preserving their loaded `.replies`/pagination cursors), then append any
 * genuinely new children the refresh surfaced.
 */
const mergeChildTrees = (existing = [], fetched = []) => {
  const fetchedById = new Map(fetched.map((c) => [c.platform_item_id, c]));
  const existingIds = new Set(existing.map((c) => c.platform_item_id));
  const merged = existing.map((prev) => {
    const fresh = fetchedById.get(prev.platform_item_id);
    if (!fresh) return prev;
    return {
      ...fresh,
      replies: prev.replies || [],
      has_more_children: prev.has_more_children,
      _childrenCursor: prev._childrenCursor,
      // Preserve client-only state so an exhausted node is not resurrected by
      // the 5 s poll (which would restart the fetch/expand cycle).
      _childrenExhausted: prev._childrenExhausted,
      _loadingMore: prev._loadingMore,
    };
  });
  for (const fresh of fetched) {
    if (!existingIds.has(fresh.platform_item_id)) merged.push(fresh);
  }
  return merged;
};

/**
 * Append replies to a node's `replies` list WITHOUT duplicating ids.
 *
 * This MUST be idempotent. The merge used to be a blind
 * `[...existing, ...incoming]`, so any repeat (a re-clicked expand button, the
 * 5 s poll, a retry after a partial failure) appended the SAME children again.
 * Because every node renders its own `.replies` subtree, duplicated nodes
 * compound — a single root reached 115,000 rendered `.comment-thread` elements
 * and the UI walk never terminated. Upsert-by-id makes the merge idempotent.
 */
const appendUniqueReplies = (existing = [], incoming = []) => {
  const byId = new Map(existing.map((c) => [c.platform_item_id, c]));
  const out = [...existing];
  for (const node of incoming) {
    const prev = byId.get(node.platform_item_id);
    if (prev) {
      // Merge server metadata but PRESERVE client-only state: already-expanded
      // nested replies, pagination cursors, and the exhausted/in-flight flags.
      // Dropping these made the expand button reappear after every poll/merge,
      // so the node was re-clicked and re-fetched forever.
      const idx = out.indexOf(prev);
      const mergedNode = {
        ...node,
        replies: prev.replies || [],
        has_more_children: prev.has_more_children,
        _childrenCursor: prev._childrenCursor,
        _childrenExhausted: prev._childrenExhausted,
        _loadingMore: prev._loadingMore,
      };
      if (idx >= 0) out[idx] = mergedNode;
      byId.set(node.platform_item_id, mergedNode);
    } else {
      out.push(node);
      byId.set(node.platform_item_id, node);
    }
  }
  return out;
};

const Inbox = () => {
  const [activeTab, setActiveTab] = useState('messages');
  // Ref for polling to check current tab without stale closure
  const activeTabRef = useRef(activeTab);
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);
  const [isComposeModalOpen, setIsComposeModalOpen] = useState(false);
  // ALL inbox items - never filtered, always complete
  const [allItems, setAllItems] = useState([]);
  // IDs we've seen - used by polling to detect new items
  const allItemIdsRef = useRef(new Set());
  // Separate selected item per tab type - persists across tab switches
  const [selectedByTab, setSelectedByTab] = useState({
    messages: null,
    comments: null,
    mentions: null,
  });
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
  const selectedItem = selectedByTab[activeTab];
  const setSelectedItem = (tabOrItem, item) => {
    // Support: setSelectedItem(item), setSelectedItem('comments', item), setSelectedItem(prev => ...), setSelectedItem('comments', prev => ...)
    if (typeof tabOrItem === 'string') {
      // setSelectedItem('comments', item) or setSelectedItem('comments', prev => ...)
      if (typeof item === 'function') {
        setSelectedByTab(prev => ({ ...prev, [tabOrItem]: item(prev[tabOrItem]) }));
      } else {
        setSelectedByTab(prev => ({ ...prev, [tabOrItem]: item }));
      }
    } else if (typeof tabOrItem === 'function') {
      // setSelectedItem(prev => ...)
      setSelectedByTab(prev => ({ ...prev, [activeTab]: tabOrItem(prev[activeTab]) }));
    } else {
      // setSelectedItem(item)
      setSelectedByTab(prev => ({ ...prev, [activeTab]: tabOrItem }));
    }
  };

  // Optimistically mark a head item read in local state (server is authoritative).
  const setHeadReadLocally = useCallback((itemId) => {
    setAllItems(prev => {
      const clicked = prev.find(i => i.id === itemId);
      // Conversation is the badge unit. Normally there is one head per
      // conversation, but matching the conversation as well keeps the badge
      // correct when a provider returns multiple root representations.
      return prev.map(i => (
        i.id === itemId || (clicked?.conversation_id && i.conversation_id === clicked.conversation_id)
      ) ? { ...i, is_read: true, unread_count: 0 } : i);
    });
  }, []);

  // Optimistically mark every message head in a conversation read.
  const setThreadReadLocally = useCallback((threadId) => {
    setAllItems(prev => prev.map(i =>
      (i.item_type === 'message' && i.platform_thread_id === threadId)
        ? { ...i, is_read: true, unread_count: 0 }
        : i
    ));
  }, []);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [activePlatforms, setActivePlatforms] = useState(null);
  // D7: which tabs the connected platforms actually support. Derived from the
  // backend capabilities (can_messages/can_comment/can_mentions) so a platform
  // without DMs (threads/linkedin/tiktok/youtube) never shows a dead Messages tab.
  const [supportedTabs, setSupportedTabs] = useState({ messages: true, comments: true, mentions: true });
  
  // Refs for loading state - used in closures to avoid stale captures
  const loadingRef = useRef(loading);
  const loadingMoreRef = useRef(false);
  const syncingRef = useRef(false);

  // Keep refs in sync with state
  useEffect(() => { loadingRef.current = loading; }, [loading]);
  useEffect(() => { loadingMoreRef.current = loadingMore; }, [loadingMore]);
  useEffect(() => { syncingRef.current = syncing; }, [syncing]);

  // Expose state for e2e tests to verify component readiness
  useEffect(() => {
    window.__inboxPageState = {
      activePlatforms,
      activeTab,
      itemsCount: allItems.length,
      loading
    };
  }, [activePlatforms, activeTab, allItems.length, loading]);

  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [stats, setStats] = useState({ total: 0, unread: 0 });
  const [showRawResponse, setShowRawResponse] = useState(false);
  const [viewingRawResponseItem, setViewingRawResponseItem] = useState(null);

  // Badge shows unread count per type - items are unread until user clicks them
  const lastPolledAtRef = useRef(Date.now());
  const isPollingRef = useRef(false);

  // Non-webhook platform sync state - track when we last synced each platform
  const lastNonWebhookSyncRef = useRef({});

  // Tab badges count CONVERSATIONS with unread items — not the sum of every
  // nested reply. This matches Agorapulse/Sprout: a post with 40 replies is ONE
  // unread conversation (badge +1); the per-reply detail lives inside the thread
  // row (`unread_count`), not aggregated into the tab.
  const unreadByType = useMemo(() => {
    const filterByPlatforms = (items) => {
      if (Array.isArray(activePlatforms) && activePlatforms.length > 0) {
        return items.filter(i => activePlatforms.includes(i.platform));
      }
      return items;
    };
    const countUnreadConversations = (items) =>
      items.filter(i => (i.unread_count || 0) > 0).length;
    return {
      messages: countUnreadConversations(filterByPlatforms(allItems.filter(i => i.item_type === 'message'))),
      comments: countUnreadConversations(filterByPlatforms(allItems.filter(i => i.item_type === 'comment'))),
      mentions: countUnreadConversations(filterByPlatforms(allItems.filter(i => i.item_type === 'mention'))),
    };
  }, [allItems, activePlatforms]);

  // D7: resolve supported tabs from the connected platforms' capabilities.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const conns = await platformsAPI.getConnections();
        if (cancelled || !Array.isArray(conns)) return;
        const relevant = (Array.isArray(activePlatforms) && activePlatforms.length > 0)
          ? conns.filter(c => activePlatforms.includes(c.platform))
          : conns;
        const tabs = { messages: false, comments: false, mentions: false };
        for (const c of relevant) {
          const caps = c.capabilities || {};
          tabs.messages = tabs.messages || !!caps.can_messages;
          tabs.comments = tabs.comments || !!caps.can_comment;
          tabs.mentions = tabs.mentions || !!caps.can_mentions;
        }
        setSupportedTabs(tabs);
      } catch (error) {
        console.error('Error loading connection capabilities:', error);
      }
    })();
    return () => { cancelled = true; };
  }, [activePlatforms]);

  // D7: if the active tab becomes unsupported, fall back to the first supported one.
  useEffect(() => {
    if (!supportedTabs[activeTab]) {
      const firstSupported = ['messages', 'comments', 'mentions'].find(t => supportedTabs[t]);
      if (firstSupported) setActiveTab(firstSupported);
    }
  }, [supportedTabs, activeTab]);
  const isSyncingNonWebhookRef = useRef(false);
  const [nonWebhookSyncing, setNonWebhookSyncing] = useState(false);

  // Refs to access current selected items per tab in event handlers (avoids stale closures)
  const selectedByTabRef = useRef({ messages: null, comments: null, mentions: null });

  // Infinite scroll state (cursor-based)
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const sentinelRef = useRef(null);

  // Conversation messages state (cursor-based pagination)
  const [conversationMessages, setConversationMessages] = useState([]);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [conversationError, setConversationError] = useState(null);
  const [conversationCursor, setConversationCursor] = useState(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const messagesSentinelRef = useRef(null);
  const messagesScrollRef = useRef(null);
  const initialAnchoringDoneRef = useRef(false);

  const handleComposeClick = () => {
    setIsComposeModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsComposeModalOpen(false);
  };

  const handleSubmitPost = async (postData, postId = null) => {
    try {
      if (postId) {
        await postsAPI.update(postId, postData);
        showToast('Post updated successfully');
      } else {
        await postsAPI.create(postData);
        showToast('Post created successfully');
      }
    } catch (error) {
      console.error('Error saving post:', error);
      showErrorToast(`Failed to save post: ${error.message || error}`);
      throw error;
    }
  };

  /**
   * Filter all items by tab type and active platforms (CLIENT-SIDE ONLY).
   * Never triggers a fetch - just filters stored data.
   * Returns persistent lists for each tab - no remounting on tab switch.
   */
  const itemsByTab = useMemo(() => {
    const filterByPlatforms = (items) => {
      if (Array.isArray(activePlatforms) && activePlatforms.length > 0) {
        return items.filter(i => activePlatforms.includes(i.platform));
      }
      return items;
    };
    return {
      messages: filterByPlatforms(allItems.filter(i => i.item_type === 'message')),
      comments: filterByPlatforms(allItems.filter(i => i.item_type === 'comment')),
      mentions: filterByPlatforms(allItems.filter(i => i.item_type === 'mention')),
    };
  }, [allItems, activePlatforms]);
  // For backward compatibility with existing code
  const visibleItems = itemsByTab[activeTab];

  /**
   * Fetch inbox items from backend. Used for initial load, infinite scroll, and polling.
   * 
   * @param {boolean} forPolling - If true, merges new items into allItems state
   */
  const fetchInboxPage = useCallback(async (pageCursor, append = false, forPolling = false) => {
    if (!append && !forPolling) setLoading(true);
    try {
      const params = {
        // Always fetch ALL types - tabs filter client-side
        type: null,
        // Restrict to the platforms the user has selected. Without this the
        // page is filled with rows from OTHER connections (e.g. connecting
        // instagram_via_facebook also creates a facebook connection), and the
        // client-side platform filter then silently truncates the visible tab
        // (a 100-item page split across two connections showed only 20 of 30
        // conversations).
        platforms: Array.isArray(activePlatforms) && activePlatforms.length > 0
          ? activePlatforms
          : null,
        // Initial/history loads use normal pagination. Polling uses delta
        // mode, so its result is independent of the inbox page boundary.
        limit: forPolling ? 100 : PAGE_SIZE,
        cursor: pageCursor,
      };
      if (forPolling && lastPolledAtRef.current) {
        // Overlap the boundary slightly; the merge is ID-based, so replaying
        // one boundary window is safe and avoids losing commits that happen
        // during the previous request.
        params.since = new Date(lastPolledAtRef.current - 2000).toISOString();
      }

      const data = await inboxAPI.getItems(params);
      console.log(`[INBOX FETCH ${forPolling ? 'POLL' : 'INITIAL'}] Response:`, JSON.stringify({ count: data.items?.length, by_type: data.by_type }, null, 2));

      if (data.by_type) {
        setStats({ by_type: data.by_type });
      }

      const fetchedItems = data.items || [];

      if (forPolling) {
        // Polling mode: add new items AND update existing items whose updated_at changed
        const newItems = fetchedItems.filter(item => !allItemIdsRef.current.has(itemKey(item)));
        
        // Track which items were updated (existing item with newer updated_at)
        const updatedItemIds = new Set();
        
        setAllItems(prev => {
          const result = [];
          for (const item of fetchedItems) {
            const key = itemKey(item);
            const existing = prev.find(i => itemKey(i) === key);
            if (existing) {
              // Update existing item if backend data is newer
              const metadataChanged = existing.unread_count !== item.unread_count
                || JSON.stringify(existing.children_ids || []) !== JSON.stringify(item.children_ids || [])
                || existing.is_read !== item.is_read;
              if (!existing.updated_at || item.updated_at > existing.updated_at || metadataChanged) {
                // Merge backend changes with existing item, preserving frontend-only fields (_children, etc.)
                result.push({
                  ...existing,
                  ...item,
                  _children: existing._children,
                  _hasMoreChildren: existing._hasMoreChildren,
                  _childrenCursor: existing._childrenCursor,
                });
                updatedItemIds.add(item.platform_item_id);
              } else {
                result.push(existing);
              }
            } else {
              result.push(item);
              allItemIdsRef.current.add(key);
            }
          }
          // Add items from prev that weren't in fetchedItems (pagination)
          for (const item of prev) {
            if (!result.find(i => itemKey(i) === itemKey(item))) {
              result.push(item);
            }
          }
          // Re-sort: the merge above is ordered by the DELTA (which items the
          // last poll changed), not by inbox activity, so without this a
          // re-synced older thread bubbles above newer ones.
          return sortItemsByActivity(result);
        });
        
        if (newItems.length > 0) {
          console.log(`[INBOX POLL] Detected ${newItems.length} new items`);
        }
        if (updatedItemIds.size > 0) {
          console.log(`[INBOX POLL] Detected ${updatedItemIds.size} updated items`);
          // Server-side read state: unread children are surfaced via item.unread_count,
          // so new webhook children are counted automatically without any local set.

          // Reload children/messages for updated items that are currently selected (in any tab)
          // Only reload if children_ids changed (new children added), not just metadata updated
          updatedItemIds.forEach(id => {
            const item = fetchedItems.find(i => i.platform_item_id === id);
            if (!item) return;

            // Determine which tab this item belongs to
            const tabType = item.item_type === 'message' ? 'messages' : item.item_type + 's';
            const tabSelected = selectedByTabRef.current[tabType];

            // Comment/mention is selected → only reload children if children_ids changed
            if ((item.item_type === 'comment' || item.item_type === 'mention')
                && tabSelected && tabSelected.id === item.id) {
              const newChildrenIds = (item.children_ids || []).sort();
              const oldChildrenIds = (tabSelected.children_ids || []).sort();
              const childrenChanged = JSON.stringify(newChildrenIds) !== JSON.stringify(oldChildrenIds);
              
              if (childrenChanged) {
                console.log(`[INBOX POLL] Children changed for selected item: ${item.platform_item_id}, reloading...`);
                inboxAPI.getChildren(item.id, { limit: PAGE_SIZE }).then(data => {
                  const children = data.items || [];
                  console.log(`[INBOX POLL] Reloaded ${children.length} children for ${item.platform_item_id}`);
                  // Merge (not replace) so already-expanded subtrees survive the
                  // 5s poll. Also copy children_ids onto the selection so the
                  // change-detection above settles instead of reloading forever.
                  setSelectedItem(tabType, prev => prev && {
                    ...prev,
                    _children: mergeChildTrees(prev._children, children),
                    children_ids: item.children_ids,
                  });
                }).catch(err => console.error('[POLL] Error reloading children:', err));
              } else {
                console.log(`[INBOX POLL] No children change for ${item.platform_item_id}, skipping reload`);
              }
            }

            // Message thread updated - backend now returns children_ids,
            // so new webhook messages are counted as unread children automatically.
            // Don't mark the parent unread - only the new child should count.
            if (item.item_type === 'message') {
              // Only reload if the messages tab is ACTIVE (user is looking at it)
              // selectedByTab can persist across tab switches, so check activeTabRef too
              if (activeTabRef.current === 'messages' && tabSelected && tabSelected.platform_thread_id === item.platform_thread_id) {
                inboxAPI.getConversationMessages(item.platform_thread_id, {
                  limit: PAGE_SIZE,
                  platform_connection_id: item.platform_connection_id,
                }).then(data => {
                  setConversationMessages(data.items || []);
                  setHasMoreMessages(!!data.has_more);
                  // Mark the conversation read server-side + locally (user is viewing it).
                  setThreadReadLocally(item.platform_thread_id);
                  inboxAPI.markConversationRead(item.platform_thread_id, item.platform_connection_id)
                    .catch(err => console.error('[POLL] Error marking conversation read:', err));
                  // Scroll to bottom after loading new messages
                  setTimeout(() => {
                    if (messagesScrollRef.current) {
                      messagesScrollRef.current.scrollTop = messagesScrollRef.current.scrollHeight;
                    }
                  }, 50);
                }).catch(err => console.error('[POLL] Error reloading conversation:', err));
              }
              // Not selected or tab not active → do NOTHING. The new child is already in children_ids
              // and will be counted as unread by the unreadByType memo.
              // The parent stays read.
            }
          });
        }
      } else {
        // Initial load / infinite scroll: update allItems
        if (append) {
          setAllItems(prev => {
            // Merge by identity instead of concatenating: a cursor boundary can
            // legitimately replay an item (activity timestamps move between
            // requests), and this merge is also what runs when the last page
            // is re-requested. Concatenating produced duplicate rows.
            const byKey = new Map(prev.map(i => [itemKey(i), i]));
            fetchedItems.forEach(item => {
              const key = itemKey(item);
              const existing = byKey.get(key);
              // Keep frontend-only fields (loaded children, cursors).
              byKey.set(key, existing ? { ...existing, ...item } : item);
              allItemIdsRef.current.add(key);
            });
            return sortItemsByActivity([...byKey.values()]);
          });
          setHasMore(!!data.has_more);
          setCursor(data.next_cursor || null);
        } else {
          setAllItems(sortItemsByActivity(fetchedItems));
          fetchedItems.forEach(item => allItemIdsRef.current.add(itemKey(item)));
          setHasMore(!!data.has_more);
          setCursor(data.next_cursor || null);
        }
      }

      lastPolledAtRef.current = Date.now();
    } catch (error) {
      console.error('Error loading inbox:', error);
    } finally {
      if (!forPolling) {
        loadingMoreRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [activePlatforms]);

  /**
   * Load inbox ONCE on mount - fetches ALL items.
   * NO reloads on tab/platform changes - they filter client-side.
   */
  useEffect(() => {
    const loadAll = async () => {
      console.log('[Inbox LOAD] Initial load of ALL inbox items');
      await fetchInboxPage(null);
    };
    loadAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mark an item read server-side and optimistically update local state.
  const markItemAsRead = useCallback((item) => {
    setHeadReadLocally(item.id);
    inboxAPI.markRead(item.id).catch(err => console.error('Error marking item read:', err));
  }, [setHeadReadLocally]);

  // CommentThreadList's IntersectionObserver calls this when a comment scrolls
  // into view. A visible item in the OPEN thread means the conversation is being
  // read, so advance its read cursor. Without this, a reply loaded lazily (after
  // the initial click) would keep the conversation permanently unread.
  const handleCommentVisible = useCallback(() => {
    const sel = selectedByTabRef.current ? selectedByTabRef.current[activeTab] : null;
    if (sel && (!sel.is_read || (sel.unread_count || 0) > 0)) {
      markItemAsRead(sel);
    }
  }, [activeTab, markItemAsRead]);

  /**
   * Auto-refresh: poll every 5 seconds, detect and notify about new items.
   * Fetches ALL items from backend, compares IDs, merges new ones into allItems.
   * NEVER triggers reloads - just updates stored data.
   */
  useEffect(() => {
    const poll = async () => {
      if (isPollingRef.current || loadingRef.current || loadingMoreRef.current || syncingRef.current) return;
      isPollingRef.current = true;
      try {
        await fetchInboxPage(null, false, true);
      } catch (error) {
        console.error('[Inbox Poll] Error:', error);
      } finally {
        isPollingRef.current = false;
      }
    };

    const interval = setInterval(poll, AUTO_REFRESH_MS);
    // Stable - fetchInboxPage has no deps, refs used internally
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Auto-sync for ALL connected platforms (webhook and non-webhook alike).
   *
   * Relying on webhooks alone stranded platforms: a webhook-kind platform with
   * no installed subscription never refreshed from the client. We therefore poll
   * every platform on an interval. The backend dedupes/rate-limits per connection
   * and webhooks continue to deliver their events untouched. SyncCheckpoint
   * tracks per-connection/per-stream last_synced_at, so each sync only fetches
   * NEW items since last time via the "since" parameter.
   */
  useEffect(() => {
    const syncAllPlatforms = async () => {
      // Use refs to avoid stale closure trap
      if (isSyncingNonWebhookRef.current || loadingRef.current || loadingMoreRef.current) return;

      isSyncingNonWebhookRef.current = true;
      setNonWebhookSyncing(true);

      try {
        // Omit `platforms` — the backend syncs every active connection, so webhook
        // platforms (and any newly connected account) are covered without
        // maintaining a client-side allow-list here.
        console.log('[Inbox Auto-Sync] Syncing all connected platforms');
        const result = await inboxAPI.sync({ mode: 'incremental' });

        // Log results for debugging
        if (result.results) {
          result.results.forEach(r => {
            if (r.status === 'failed' && r.error) {
              console.error(`[Inbox Auto-Sync] ${r.platform} failed:`, r.error);
            } else if (r.status === 'skipped_rate_limit') {
              console.log(`[Inbox Auto-Sync] ${r.platform} skipped (rate limited, retry in ${r.retry_after}s)`);
            } else {
              console.log(`[Inbox Auto-Sync] ${r.platform}: ${r.status}`);
            }
            lastNonWebhookSyncRef.current[r.platform] = Date.now();
          });
        }
      } catch (error) {
        console.error('[Inbox Auto-Sync] Error:', error);
      } finally {
        isSyncingNonWebhookRef.current = false;
        setNonWebhookSyncing(false);
      }
    };

    const interval = setInterval(syncAllPlatforms, NON_WEBHOOK_SYNC_MS);
    return () => clearInterval(interval);
  }, []); // No deps - syncs ALL platforms regardless of UI filter

  /**
   * Infinite scroll: load next page when sentinel is visible.
   */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingRef.current && !loadingMoreRef.current) {
          // Set the ref synchronously: state updates land after this callback,
          // so two intersecting callbacks in the same tick could otherwise
          // request the SAME cursor twice and append the page twice.
          loadingMoreRef.current = true;
          setLoadingMore(true);
          fetchInboxPage(cursor, true);
        }
      },
      { rootMargin: '200px' }
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }
    return () => observer.disconnect();
  }, [cursor, hasMore, fetchInboxPage]);

  const handleSync = async () => {
    // Sync ALL connected platforms - activePlatforms is UI filter only, irrelevant for sync
    setSyncing(true);
    try {
      const syncResult = await inboxAPI.sync({});
      console.log('[INBOX SYNC] Sync result:', JSON.stringify(syncResult, null, 2));
      // Fetch all items again and merge into allItems (no reload, just update stored data)
      const data = await inboxAPI.getItems({
        type: null,
        limit: 100,
        // Scope to the visible platforms so sibling connections (e.g. the
        // facebook connection auto-created by instagram_via_facebook) don't
        // consume the page budget and truncate the visible tab.
        platforms: Array.isArray(activePlatforms) && activePlatforms.length > 0
          ? activePlatforms
          : null,
      });
      console.log('[INBOX SYNC] GetItems response:', JSON.stringify({ count: data.items?.length, by_type: data.by_type }, null, 2));
      const fetchedItems = data.items || [];

      // Merge against the LATEST state inside the updater. Computing the
      // dedup set from the closure `allItems` here would be stale: the 5s
      // poll can add items while `sync()` is awaited, and those would then be
      // re-added as duplicates (e.g. 14 polled + 30 fetched = 44).
      setAllItems(prev => {
        const existingIds = new Set(prev.map(i => itemKey(i)));
        const newItems = fetchedItems.filter(item => !existingIds.has(itemKey(item)));
        console.log('[INBOX SYNC] Existing items:', prev.length, 'Fetched:', fetchedItems.length, 'New:', newItems.length);
        if (newItems.length === 0) return prev;
        newItems.forEach(item => allItemIdsRef.current.add(itemKey(item)));
        return sortItemsByActivity([...prev, ...newItems]);
      });

      showToast('Inbox synced successfully');
    } catch (error) {
      console.error('[INBOX SYNC] Error syncing inbox:', error);
      showErrorToast(`Sync failed: ${error.message || error}`, 3000);
    } finally {
      setSyncing(false);
    }
  };

  // Mark all items read server-side + optimistically.
  const handleMarkAllRead = useCallback(async () => {
    console.log('[INBOX MARK-ALL-READ] Clicked. allItems:', allItems.length);
    setAllItems(prev => prev.map(i => ({ ...i, is_read: true, unread_count: 0 })));
    try {
      await inboxAPI.markAllRead();
    } catch (error) {
      console.error('[INBOX MARK-ALL-READ] Error marking all read:', error);
      showErrorToast('Failed to mark all as read');
    }
  }, [allItems]);

  /**
   * Handle clicking an inbox item.
   * - Messages: load conversation messages via /conversation/:thread_id
   * - Comments/Mentions: open CommentsPanel for the post context
   */
  const handleItemClick = async (item) => {
    setMobileDetailOpen(true);
    // Map item_type to tab name (plural to match tab-panel data-tab attributes)
    const tabType = item.item_type === 'message' ? 'messages' : item.item_type + 's';
    console.log(`[INBOX CLICK] item.id=${item.id}, platform_item_id=${item.platform_item_id}, type=${item.item_type}, tab=${tabType}`);
    setReplyText('');

    // Mark as read if the head or any descendant is still unread
    if (!item.is_read || (item.unread_count || 0) > 0) {
      console.log(`[INBOX CLICK] Marking thread as read: ${item.platform_item_id}`);
      markItemAsRead(item);
    } else {
      console.log(`[INBOX CLICK] Already read: ${item.platform_item_id}`);
    }

    // Load children/messages based on item type
    if (item.item_type === 'message') {
      // Select this conversation first so right panel shows it
      selectedByTabRef.current[tabType] = item;
      setSelectedItem(tabType, item);

      // Load conversation messages (first page)
      const threadId = item.platform_thread_id;
      if (threadId) {
        // Clear the previous thread's messages immediately. Otherwise a slow
        // or failed load leaves the OLD conversation visible under the new
        // header (what made a 900+ message thread look like the last one).
        setConversationMessages([]);
        setConversationError(null);
        setConversationCursor(null);
        setHasMoreMessages(true);
        setLoadingConversation(true);
        try {
          const data = await inboxAPI.getConversationMessages(threadId, {
            limit: PAGE_SIZE,
            platform_connection_id: item.platform_connection_id,
          });
          setConversationMessages(data.items || []);
          setHasMoreMessages(!!data.has_more);
          // Persist the cursor for the NEXT (older) page. Without this the
          // first "load older" call passes cursor=null, re-fetches page 1 and
          // re-prepends it — the older history then never renders.
          setConversationCursor(data.next_cursor || null);
          // Mark the whole conversation read (server + local).
          setThreadReadLocally(threadId);
          inboxAPI.markConversationRead(threadId, item.platform_connection_id)
            .catch(err => console.error('Error marking conversation read:', err));
        } catch (error) {
          console.error('Error loading conversation:', error);
          setConversationError(
            error.message || 'Failed to load this conversation. Please try again.'
          );
        } finally {
          setLoadingConversation(false);
        }
      }
    } else if (item.item_type === 'comment' || item.item_type === 'mention') {
      // Check if this is the same item already selected with children loaded
      const existingSelected = selectedByTabRef.current[tabType];
      const sameItem = existingSelected && existingSelected.id === item.id && existingSelected._children && existingSelected._children.length > 0;
      
      // Only reload children if: not same item, or children_ids changed (new children arrived)
      const childrenChanged = sameItem && 
        JSON.stringify((item.children_ids || []).sort()) !== JSON.stringify((existingSelected.children_ids || []).sort());
      
      if (sameItem && !childrenChanged) {
        // Same item, children unchanged — just update selection ref without reloading
        console.log(`[INBOX CLICK] Same item selected, children unchanged — skipping reload for ${item.id}`);
        selectedByTabRef.current[tabType] = existingSelected;
      } else {
        // Different item or children changed — load children
        // For different item, set selection first. For same item with changed children, update with fresh backend data.
        if (!sameItem) {
          selectedByTabRef.current[tabType] = item;
          setSelectedItem(tabType, item);
        }
        console.log(`[INBOX CLICK] Loading children for ${item.id} (sameItem=${sameItem}, childrenChanged=${childrenChanged})`);
        try {
          // D8: page the first load like a nested platform (PAGE_SIZE) instead of
          // silently pulling everything (the old default was limit=100).
          const data = await inboxAPI.getChildren(item.id, { limit: PAGE_SIZE });
          const children = data.items || [];
          console.log(`[INBOX CLICK] Loaded ${children.length} children for ${item.id} (will mark read on visibility)`);
          // Merge (not replace) so a refresh of the same item keeps any
          // expanded subtrees/paginated pages already loaded.
          setSelectedItem(tabType, prev => prev && {
            ...prev,
            _children: mergeChildTrees(prev._children, children),
            _hasMoreChildren: prev._hasMoreChildren || !!data.has_more,
            _childrenCursor: prev._childrenCursor || data.next_cursor || null,
            children_ids: item.children_ids,
          });
        } catch (error) {
          console.error('Error loading children:', error);
        }
      }
    } else {
      // For other types, set selection
      selectedByTabRef.current[tabType] = item;
      setSelectedItem(tabType, item);
    }
  };

  // Keep selectedByTabRef in sync with selectedByTab state
  useEffect(() => {
    selectedByTabRef.current = selectedByTab;
  }, [selectedByTab]);

  /**
   * Load more messages for the selected conversation (infinite scroll).
   * Loads OLDER messages (going up) — prepend to the list.
   * Preserves scroll position when prepending.
   */
  const loadMoreMessages = useCallback(async () => {
    if (!selectedItem?.platform_thread_id || !hasMoreMessages) return;
    setLoadingMoreMessages(true);
    try {
      const data = await inboxAPI.getConversationMessages(
        selectedItem.platform_thread_id,
        { cursor: conversationCursor, limit: PAGE_SIZE, platform_connection_id: selectedItem.platform_connection_id }
      );
      const olderItems = data.items || [];

      // Preserve scroll position when prepending older messages
      const scrollEl = messagesScrollRef.current;
      const oldScrollTop = scrollEl ? scrollEl.scrollTop : 0;
      const oldHeight = scrollEl ? scrollEl.scrollHeight : 0;

      // Prepend older messages (they come before current ones chronologically).
      // De-dupe by platform_item_id: a transient stale cursor can make the
      // backend return a page we already hold, and duplicate React keys
      // collapse in the DOM (the thread appears to never grow).
      setConversationMessages(prev => {
        const seen = new Set(prev.map((m) => m.platform_item_id));
        const fresh = olderItems.filter((m) => !seen.has(m.platform_item_id));
        return [...fresh, ...prev];
      });
      setConversationCursor(data.next_cursor || null);
      setHasMoreMessages(!!data.has_more);

      // Restore scroll position after prepend
      requestAnimationFrame(() => {
        if (scrollEl) {
          const newHeight = scrollEl.scrollHeight;
          scrollEl.scrollTop = oldScrollTop + (newHeight - oldHeight);
        }
      });
    } catch (error) {
      console.error('Error loading more messages:', error);
      showErrorToast(`Failed to load older messages: ${error.message || error}`);
    } finally {
      setLoadingMoreMessages(false);
    }
  }, [selectedItem, hasMoreMessages, conversationCursor]);

  /**
   * Scroll to bottom after initial conversation load (newest messages at bottom).
   * Uses a per-conversation ref so a one-page conversation (cursor=null) doesn't
   * re-anchor when message length changes.
   */
  useEffect(() => {
    if (!loadingConversation && conversationMessages.length > 0 && !initialAnchoringDoneRef.current && messagesScrollRef.current) {
      const el = messagesScrollRef.current;
      el.scrollTop = el.scrollHeight;
      initialAnchoringDoneRef.current = true;
    }
  }, [loadingConversation, conversationMessages.length]);

  // Reset anchoring ref when conversation changes
  useEffect(() => {
    if (selectedItem) {
      initialAnchoringDoneRef.current = false;
    }
  }, [selectedItem?.platform_thread_id]);

  /**
   * Infinite scroll for conversation messages: load older messages when sentinel is visible.
   */
  useEffect(() => {
    if (selectedItem?.item_type !== 'message') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreMessages && !loadingConversation && !loadingMoreMessages) {
          loadMoreMessages();
        }
      },
      { root: messagesScrollRef.current, rootMargin: '100px' }
    );

    if (messagesSentinelRef.current) {
      observer.observe(messagesSentinelRef.current);
    }
    return () => observer.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem, hasMoreMessages, loadingConversation, loadingMoreMessages]);

  /**
   * Load more children for the top-level selected item (cursor-based).
   *
   * In-flight guarded: the "Load more replies" button must not fire duplicate
   * concurrent requests. Without `_loadingMore` a slow link (or a double click)
   * issues the SAME cursor page twice; `appendUniqueReplies` keeps the rows
   * idempotent, but the wasted requests and any out-of-order cursor response
   * could still drop a page. The flag is set before the fetch and cleared in
   * BOTH the success and error paths, matching the nested
   * `handleLoadMoreReplies` path.
   */
  const loadMoreChildren = async () => {
    if (!selectedItem?._children) return;
    if (selectedItem._loadingMore) return;
    setSelectedItem(prev => prev && { ...prev, _loadingMore: true });
    try {
      const data = await inboxAPI.getChildren(selectedItem.id, {
        cursor: selectedItem._childrenCursor,
        limit: PAGE_SIZE
      });
      setSelectedItem(prev => ({
        ...prev,
        _loadingMore: false,
        _children: appendUniqueReplies(prev._children || [], data.items || []),
        _hasMoreChildren: !!data.has_more,
        _childrenCursor: data.next_cursor || null,
      }));
    } catch (error) {
      console.error('Error loading more children:', error);
      // Clear the flag so the button is retryable (a failure is not exhaustion).
      setSelectedItem(prev => prev && { ...prev, _loadingMore: false });
    }
  };

  /**
   * Load replies for ANY comment (used by CommentThreadList for recursive expand).
   * Uses cursor from comment._childrenCursor for continuation.
   */
  const handleLoadMoreReplies = useCallback(async (commentId) => {
    const sameId = (left, right) => left !== null && left !== undefined
      && right !== null && right !== undefined
      && String(left) === String(right);
    // Find the comment to get its cursor
    let targetCursor = null;
    let targetNode = null;
    let isRoot = false;
    if (sameId(selectedItem?.id, commentId)
      || selectedItem?.platform_item_id === commentId) {
      targetCursor = selectedItem._childrenCursor || null;
      targetNode = selectedItem;
      isRoot = true;
    }
    const findCursor = (children) => {
      for (const c of children) {
        if (c.id === commentId || String(c.id) === String(commentId) || c.platform_item_id === commentId) {
          targetCursor = c._childrenCursor || null;
          targetNode = c;
          return true;
        }
        if (c.replies && c.replies.length > 0) {
          if (findCursor(c.replies)) return true;
        }
      }
      return false;
    };
    if (!targetNode && selectedItem?._children) {
      findCursor(selectedItem._children);
    }

    // Exhausted guard: a nested node keeps its "Load more replies" button even
    // after its replies are fully loaded (nested nodes always show one so a
    // lazy fetch can be retried). Once exhausted the cursor is null, so another
    // click would re-request page 1 and append DUPLICATE replies. If the node
    // has replies, reports no more, and has no cursor, there is nothing to load.
    //
    // Mark it exhausted rather than returning silently: an early `return` left
    // the button ENABLED, so a caller that keeps clicking expandable nodes (the
    // exhaustive E2E walk, or a user) re-triggered this no-op forever and the
    // button never went away.
    if (!isRoot && targetNode && targetNode.has_more_children === false
      && !targetCursor && (targetNode.replies || []).length > 0) {
      setSelectedItem(prev => prev && {
        ...prev,
        _children: setNodeFlag(prev._children || [], commentId, '_childrenExhausted', true),
      });
      return;
    }

    // Mark this node in-flight. The button is `disabled={comment._loadingMore}`;
    // that flag used to be read but NEVER written, so the button stayed enabled
    // during a fetch and every click (and every poll) fired another request.
    setSelectedItem(prev => {
      if (!prev) return prev;
      const isSelectedRoot = sameId(prev.id, commentId)
        || prev.platform_item_id === commentId;
      if (isSelectedRoot) {
        return { ...prev, _loadingMore: true };
      }
      return {
        ...prev,
        _children: setNodeFlag(prev._children || [], commentId, '_loadingMore', true),
      };
    });

    try {
      const data = await inboxAPI.getChildren(commentId, { cursor: targetCursor, limit: PAGE_SIZE });
      const items = data.items || [];
      // No next page means there is literally nothing more to load, so the
      // node is EXHAUSTED and its expand button must disappear. This covers
      // both a leaf that returned no children and a node whose final reply page
      // has just been rendered. Without the flag the button survives and is
      // re-clicked (and re-fetched) on every pass — the fetch storm that
      // exhausted the browser and made an exhaustive UI walk never terminate.
      const exhausted = !data.has_more;
      // Update the selected item's children tree to include the new replies
      setSelectedItem(prev => {
        if (!prev) return prev;
        const isSelectedRoot = sameId(prev.id, commentId)
          || prev.platform_item_id === commentId;
        if (isSelectedRoot) {
          return {
            ...prev,
            _loadingMore: false,
            _children: appendUniqueReplies(prev._children || [], items),
            _hasMoreChildren: !!data.has_more,
            _childrenCursor: data.next_cursor || null,
            _childrenExhausted: exhausted,
          };
        }
        let updatedChildren = updateCommentReplies(
          prev._children || [],
          commentId,
          items,
          !!data.has_more,
          data.next_cursor || null
        );
        updatedChildren = setNodeFlag(updatedChildren, commentId, '_loadingMore', false);
        if (exhausted) {
          updatedChildren = setNodeFlag(updatedChildren, commentId, '_childrenExhausted', true);
        }
        return {
          ...prev,
          _children: updatedChildren,
        };
      });
    } catch (error) {
      console.error('Error loading replies:', error);
      // Clear the in-flight flag so the node can be retried (do NOT mark it
      // exhausted — a failed fetch is not proof that it has no children).
      setSelectedItem(prev => {
        if (!prev) return prev;
        const isSelectedRoot = sameId(prev.id, commentId)
          || prev.platform_item_id === commentId;
        if (isSelectedRoot) {
          return { ...prev, _loadingMore: false };
        }
        return {
          ...prev,
          _children: setNodeFlag(prev._children || [], commentId, '_loadingMore', false),
        };
      });
    }
  }, [selectedItem]);

  /**
   * Set a flag on the node matching `targetId` anywhere in the nested tree.
   * Used for transient (_loadingMore) and terminal (_childrenExhausted) state.
   */
  const setNodeFlag = (children, targetId, flag, value) => {
    const same = (left, right) => left !== null && left !== undefined
      && right !== null && right !== undefined && String(left) === String(right);
    const mapNode = (node) => {
      const cloned = { ...node, replies: (node.replies || []).map(mapNode) };
      if (same(cloned.id, targetId) || cloned.platform_item_id === targetId) {
        cloned[flag] = value;
      }
      return cloned;
    };
    return children.map(mapNode);
  };

  /**
   * Inject loaded replies into nested children array using O(n) Map — no recursive tree walk.
   */
  const updateCommentReplies = (children, targetId, newReplies, hasMore, nextCursor) => {
    // Recursive clone-and-update. `children` is the flat list of top-level
    // replies, but prior expansions nest their children in each node's
    // `.replies`. A flat byId map keyed only on top-level ids misses depth-2+
    // targets, so walk the tree recursively to find and extend the right node.
    const mapNode = (node) => {
      const cloned = { ...node, replies: (node.replies || []).map(mapNode) };
      if (cloned.id === targetId
        || String(cloned.id) === String(targetId)
        || cloned.platform_item_id === targetId) {
        // Idempotent merge: re-expanding a node must not duplicate its replies.
        cloned.replies = appendUniqueReplies(cloned.replies, newReplies);
        cloned.has_more_children = hasMore;
        cloned._childrenCursor = nextCursor;
      }
      return cloned;
    };
    return children.map(mapNode);
  };

  // Reply handler for messages (single item reply)
  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedItem) return;

    setSendingReply(true);
    try {
      // selectedItem.id is always the InboxItem ID now
      await inboxAPI.reply(selectedItem.id, replyText);
      setReplyText('');
      // Reload conversation messages
      if (selectedItem.item_type === 'message' && selectedItem.platform_thread_id) {
        const data = await inboxAPI.getConversationMessages(selectedItem.platform_thread_id);
        setConversationMessages(data.items || []);
        setThreadReadLocally(selectedItem.platform_thread_id);
      }
      showToast('Reply sent successfully');
    } catch (error) {
      console.error('Error sending reply:', error);
      showErrorToast(`Failed to send reply: ${error.message}`);
    } finally {
      setSendingReply(false);
    }
  };

  // Reply handler for comment threads (passed to CommentThreadList)
  const handleCommentReply = useCallback(async (commentId, text) => {
    try {
      await inboxAPI.reply(commentId, text);
      // Reload children — they'll be marked read only when scrolled into view.
      // MERGE (not replace): a bare first page would collapse any subtree the
      // user already expanded deeper (the exact bug mergeChildTrees exists to
      // prevent). Keep existing nodes + paginated pages, append the new reply.
      if (selectedItem && selectedItem.id) {
        const data = await inboxAPI.getChildren(selectedItem.id, { limit: PAGE_SIZE });
        const children = data.items || [];
        console.log(`[INBOX REPLY] Reloaded ${children.length} children after reply for ${selectedItem.platform_item_id}`);
        setSelectedItem(prev => prev && {
          ...prev,
          _children: mergeChildTrees(prev._children, children),
          _hasMoreChildren: prev._hasMoreChildren || !!data.has_more,
          _childrenCursor: prev._childrenCursor || data.next_cursor || null,
        });
      }
      showToast('Reply sent successfully');
    } catch (error) {
      console.error('Error sending reply:', error);
      showErrorToast(`Failed to send reply: ${error.message}`);
      throw error;
    }
  }, [selectedItem]);

  const handleArchive = async (itemId) => {
    try {
      await inboxAPI.archive(itemId);
      // Remove from allItems directly - no reload needed
      setAllItems(prev => {
        const item = prev.find(i => i.id === itemId);
        if (item) allItemIdsRef.current.delete(item.platform_item_id);
        return prev.filter(i => i.id !== itemId);
      });
      if (selectedItem?.id === itemId) {
        setSelectedItem(null);
        setConversationMessages([]);
      }
    } catch (error) {
      console.error('Error archiving:', error);
      // Fallback: update local state if API fails
      setAllItems(prev => prev.filter(i => i.id !== itemId));
      if (selectedItem?.id === itemId) {
        setSelectedItem(null);
        setConversationMessages([]);
      }
    }
  };

  const handlePlatformToggle = useCallback((platforms) => {
    console.log('[Inbox PLATFORM TOGGLE] Setting activePlatforms:', platforms);
    setActivePlatforms(platforms);
  }, []);

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;

    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return date.toLocaleDateString();
  };

  // Render a conversation list for a given tab type (persistent - doesn't remount on tab switch)
  const renderConvoList = (tabType, items, selected) => {
    if (loading) {
      return <div className="loading-state">Loading...</div>;
    }
    if (items.length === 0) {
      return (
        <div className="empty-state">
          <p>No {tabType} yet</p>
          <button onClick={handleSync} className="sync-link">
            Sync your inbox
          </button>
        </div>
      );
    }
    return (
      <>
        {items.map((item) => {
          const isActive = selected?.id === item.id;
          const hasUnread = (item.unread_count || 0) > 0;
          const preview = (item.last_item_preview || item.content || '');

          return (
            <div
              key={item.id}
              className={`convo ${isActive ? 'active' : ''} ${hasUnread ? 'unread' : ''}`}
              data-thread-id={item.platform_thread_id || undefined}
              data-item-id={item.platform_item_id || undefined}
              data-item-type={item.item_type}
              onClick={() => handleItemClick(item)}
            >
              <div className="avatar-container">
                {conversationAvatar(item) ? (
                  <img src={conversationAvatar(item)} alt="" className="avatar" />
                ) : (
                  <div className="avatar-placeholder">
                    {(conversationTitle(item) || '?')[0].toUpperCase()}
                  </div>
                )}
                <span
                  className="platform-badge"
                  style={{ backgroundColor: PLATFORM_COLORS[item.platform] || '#666' }}
                  title={item.platform}
                >
                  {item.platform[0].toUpperCase()}
                </span>
              </div>
              <div className="convo-content">
                {item.item_type === 'message' ? (
                  <>
                    <h4>{conversationTitle(item)}</h4>
                    <p className="preview">{preview.slice(0, 60)}{preview.length > 60 ? '...' : ''}</p>
                    <p className="thread-meta">
                      {(item.child_count || 0)} message{(item.child_count || 0) !== 1 ? 's' : ''}
                      {(item.unread_count || 0) > 0 && ` • ${item.unread_count} unread`}
                    </p>
                  </>
                ) : (
                  <>
                    <h4>{item.sender_display_name || item.sender_username || 'Unknown'}</h4>
                    <p className="preview">{preview.slice(0, 60)}{preview.length > 60 ? '...' : ''}</p>
                    {item.related_post_title && (
                      <p className="post-context">on: {item.related_post_title.slice(0, 30)}...</p>
                    )}
                    <p className="thread-meta">
                      {(item.unread_count || 0) > 0 && `${item.unread_count} unread`}
                      {selected?.id === item.id && selected?._children?.length > 0 && (
                        <>
                          {(item.unread_count || 0) > 0 ? ' • ' : ''}
                          {selected._children.length} repl{selected._children.length !== 1 ? 'ies' : 'y'}
                        </>
                      )}
                    </p>
                  </>
                )}
              </div>
              <div className="convo-meta">
                <span className="time">{formatTime(item.platform_created_at)}</span>
                {hasUnread && <span className="unread-dot"></span>}
              </div>
            </div>
          );
        })}
      </>
    );
  };

  // Render right panel for a given tab type (persistent - doesn't remount on tab switch)
  const renderRightPanel = (tabType, selected) => {
    if (!selected) {
      return (
        <div className="no-selection">
          <button type="button" className="mobile-back-btn" onClick={() => setMobileDetailOpen(false)}>
            ← Inbox
          </button>
          <p>Select a conversation to view</p>
        </div>
      );
    }

    if (selected.item_type === 'message') {
      return (
        <>
          <div className="chat-header">
            <button type="button" className="mobile-back-btn" onClick={() => setMobileDetailOpen(false)}>
              ← Inbox
            </button>
            <div className="user-info">
              {conversationAvatar(selected) ? (
                <img src={conversationAvatar(selected)} alt="" className="avatar" />
              ) : (
                <div className="avatar-placeholder large">
                  {(conversationTitle(selected) || '?')[0].toUpperCase()}
                </div>
              )}
              <div>
                <h3>
                  {conversationTitle(selected)}
                  <span
                    className="platform-tag"
                    style={{ backgroundColor: PLATFORM_COLORS[selected.platform] || '#666' }}
                  >
                    {selected.platform}
                  </span>
                </h3>
                <p>{selected.counterpart_username ? `@${selected.counterpart_username}` : ''}</p>
              </div>
            </div>
            <div className="header-actions">
              <button
                className="archive-btn"
                onClick={async () => {
                  const itemToArchive = selectedByTabRef.current.messages;
                  if (!itemToArchive) return;
                  const archivedId = itemToArchive.id;
                  try {
                    await inboxAPI.archiveConversation(
                      itemToArchive.platform_thread_id,
                      itemToArchive.platform_connection_id
                    );
                  } catch (error) {
                    showErrorToast(`Archive failed: ${error.message || error}`);
                  }
                  setAllItems(prev => prev.filter(i => i.id !== archivedId));
                  setSelectedItem('messages', null);
                  setConversationMessages([]);
                }}
                title="Archive"
              >
                🗑
              </button>
            </div>
          </div>

          <div className="messages" ref={messagesScrollRef}>
            {loadingConversation ? (
              <div className="loading-state">Loading messages...</div>
            ) : conversationError ? (
              <div className="empty-state">
                <p>{conversationError}</p>
                <button
                  type="button"
                  className="sync-link"
                  onClick={() => selectedItem && handleItemClick(selectedItem)}
                >
                  Retry
                </button>
              </div>
            ) : conversationMessages.length === 0 ? (
              <div className="empty-state">No messages yet</div>
            ) : (
              <>
                <div ref={messagesSentinelRef} className="msg-sentinel">
                  {loadingMoreMessages && <div className="loading-state">Loading older messages...</div>}
                </div>
                {conversationMessages.map((msg) => (
                  <div
                    key={msg.id}
                    // Inbox.css styles `.message.own` (right-aligned + brand
                    // colour). This previously emitted `sent`/`received`, which
                    // no stylesheet matched, so every bubble rendered on the
                    // left and looked incoming.
                    className={`message ${isOutboundMessage(msg) ? 'own' : 'incoming'}`}
                    data-message-id={msg.platform_item_id || undefined}
                    data-message-side={isOutboundMessage(msg) ? 'outbound' : 'inbound'}
                  >
                    <div className="msg-content">
                      <p>
                        {msg.content || (
                          <em className="msg-unsupported">Unsupported message type</em>
                        )}
                      </p>
                      {Array.isArray(msg.media_urls) && msg.media_urls.length > 0 && (
                        <div className="msg-media">
                          {msg.media_urls.map((url, index) => (
                            <a key={url} href={url} target="_blank" rel="noreferrer">
                              <img src={url} alt={`Attachment ${index + 1}`} loading="lazy" />
                            </a>
                          ))}
                        </div>
                      )}
                      <span className="msg-time">
                        {msg.platform_created_at
                          ? new Date(msg.platform_created_at).toLocaleString()
                          : ''}
                      </span>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>

          <div className="chat-input">
            <input
              type="text"
              placeholder={`Reply on ${selected.platform}...`}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendReply()}
              disabled={sendingReply}
            />
            <button
              className="send-btn"
              onClick={handleSendReply}
              disabled={!replyText.trim() || sendingReply}
            >
              {sendingReply ? 'Sending...' : 'Reply'}
            </button>
          </div>
        </>
      );
    }

    // Comment or mention
    return (
      <>
        <div className="chat-header">
          <button type="button" className="mobile-back-btn" onClick={() => setMobileDetailOpen(false)}>
            ← Inbox
          </button>
          <div className="user-info">
            <div className="avatar-placeholder large">
              {selected.item_type === 'mention' ? '👤' : '📝'}
            </div>
            <div>
              <h3>
                {selected.related_post_title?.slice(0, 50) || selected.content?.slice(0, 50) || 'Thread'}
                {selected.related_post_title?.length > 50 ? '...' : ''}
              </h3>
              <p>
                {selected.platform} • {selected.item_type === 'mention' ? 'mention' : 'comment'}
                {selected._children?.length > 0 && ` • ${selected._children.length} repl${selected._children.length !== 1 ? 'ies' : 'y'}`}
              </p>
            </div>
          </div>
          <div className="header-actions">
            {selected.related_post_url && (
              <a
                href={selected.related_post_url}
                target="_blank"
                rel="noopener noreferrer"
                className="view-post-btn"
              >
                View Post ↗
              </a>
            )}
          </div>
        </div>

        <PostTypeIndicator
          post={selected}
          platform={selected.platform}
        />

        <CommentThreadList
          comments={[selected, ...(selected._children || [])]}
          onReply={handleCommentReply}
          onLoadMoreReplies={handleLoadMoreReplies}
          onCommentVisible={handleCommentVisible}
        />

        {selected._hasMoreChildren && (
          <div className="load-more-replies">
            <button onClick={loadMoreChildren} disabled={selected._loadingMore}>
              {selected._loadingMore ? 'Loading...' : 'Load more replies'}
            </button>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="inbox-page">
      <TopNav activePage="inbox" onComposeClick={handleComposeClick} />
      <SideIcons 
        onPlatformToggle={handlePlatformToggle}
        activePlatforms={activePlatforms}
        requireCapability="inbox"
      />

      <main className="inbox-container">
        {Array.isArray(activePlatforms) && activePlatforms.length === 0 ? (
          <div className="feature-unavailable" role="status" aria-live="polite">
            <div className="feature-unavailable-card">
              <h2>Inbox unavailable</h2>
              <p>
                None of your connected account types support inbox features for the selected platforms.
              </p>
              <p>Connect a supported account (or upgrade permissions) in Settings.</p>
            </div>
          </div>
        ) : null}

        {/* LEFT: CONVERSATION LIST - persistent panels per tab */}
        <section className={`convo-list ${mobileDetailOpen ? 'mobile-hidden' : ''}`}>
          {/* TABS */}
          <div className="tabs">
            {supportedTabs.messages && (
            <button 
              id="tab-messages"
              className={`tab ${activeTab === 'messages' ? 'active' : ''}`}
              onClick={() => setActiveTab('messages')}
            >
              Messages
              {unreadByType.messages > 0 && (
                <span id="badge-messages" className="tab-badge new-badge">+{unreadByType.messages}</span>
              )}
            </button>
            )}
            {supportedTabs.comments && (
            <button 
              id="tab-comments"
              className={`tab ${activeTab === 'comments' ? 'active' : ''}`}
              onClick={() => setActiveTab('comments')}
            >
              Comments
              {unreadByType.comments > 0 && (
                <span id="badge-comments" className="tab-badge new-badge">+{unreadByType.comments}</span>
              )}
            </button>
            )}
            {supportedTabs.mentions && (
            <button 
              id="tab-mentions"
              className={`tab ${activeTab === 'mentions' ? 'active' : ''}`}
              onClick={() => setActiveTab('mentions')}
            >
              Mentions
              {unreadByType.mentions > 0 && (
                <span id="badge-mentions" className="tab-badge new-badge">+{unreadByType.mentions}</span>
              )}
            </button>
            )}
          </div>

          {/* SYNC BUTTON & SEARCH */}
          <div className="list-controls">
             <button 
              id="mark-all-read-btn"
              className="mark-all-read-btn"
              onClick={handleMarkAllRead}
              title="Mark all items as read"
            >
              ✓
            </button>
            <button 
              className={`sync-btn ${syncing ? 'syncing' : ''}`}
              onClick={handleSync}
              disabled={syncing}
              title="Sync inbox"
            >
              {syncing ? '⟳' : '↻'} Sync
            </button>
            <input className="search" placeholder="Search conversations..." />
          </div>

          {/* CONVERSATIONS - all 3 tab lists rendered persistently, visibility toggled */}
          <div className="convos">
            <div className="tab-panel" data-tab="messages" style={{ display: activeTab === 'messages' ? 'block' : 'none' }}>
              {renderConvoList('messages', itemsByTab.messages, selectedByTab.messages)}
            </div>
            <div className="tab-panel" data-tab="comments" style={{ display: activeTab === 'comments' ? 'block' : 'none' }}>
              {renderConvoList('comments', itemsByTab.comments, selectedByTab.comments)}
            </div>
            <div className="tab-panel" data-tab="mentions" style={{ display: activeTab === 'mentions' ? 'block' : 'none' }}>
              {renderConvoList('mentions', itemsByTab.mentions, selectedByTab.mentions)}
            </div>
            {/* Infinite scroll sentinel - only visible for active tab */}
            <div ref={sentinelRef} className="sentinel">
              {loadingMore && <div className="loading-state">Loading more...</div>}
              {!loadingMore && hasMore && <div className="loading-state">Loading more...</div>}
              {!hasMore && allItems.length > 0 && <div className="end-of-inbox">End of inbox</div>}
            </div>
          </div>
        </section>

        {/* RIGHT: CHAT WINDOW - persistent panels per tab */}
        <section className={`chat-window ${mobileDetailOpen ? 'mobile-visible' : ''}`}>
          {/* Messages panel */}
          <div className={`tab-panel ${activeTab === 'messages' ? 'active' : ''}`} data-tab="messages" style={{ display: activeTab === 'messages' ? 'block' : 'none' }}>
            {renderRightPanel('messages', selectedByTab.messages)}
          </div>
          {/* Comments panel */}
          <div className={`tab-panel ${activeTab === 'comments' ? 'active' : ''}`} data-tab="comments" style={{ display: activeTab === 'comments' ? 'block' : 'none' }}>
            {renderRightPanel('comments', selectedByTab.comments)}
          </div>
          {/* Mentions panel */}
          <div className={`tab-panel ${activeTab === 'mentions' ? 'active' : ''}`} data-tab="mentions" style={{ display: activeTab === 'mentions' ? 'block' : 'none' }}>
            {renderRightPanel('mentions', selectedByTab.mentions)}
          </div>
        </section>
      </main>

      <ComposeModal 
        isOpen={isComposeModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitPost}
      />

      <RawResponseModal
        isOpen={showRawResponse}
        onClose={() => setShowRawResponse(false)}
        rawResponse={viewingRawResponseItem?.raw_response}
      />
      
      <Footer />
    </div>
  );
};

export default Inbox;
